import { useEffect, useRef, useState } from 'react';
import { RefreshCw, Undo2, RotateCcw, Clock, MailX } from 'lucide-react';
import { apiAdmin } from '@/lib/api';
import { toast } from 'sonner';

const JOB_STATUS = {
  pending: { label: 'Scheduled', cls: 'bg-amber-500/15 text-amber-300' },
  sending: { label: 'Sending', cls: 'bg-blue-500/15 text-blue-300' },
  sent: { label: 'Sent', cls: 'bg-emerald-500/15 text-emerald-300' },
  cancelled: { label: 'Cancelled', cls: 'bg-[#2A2A2A] text-[#8A8A8A]' },
  failed: { label: 'Failed', cls: 'bg-red-500/15 text-red-300' },
};

function JobStatusPill({ status }) {
  const s = JOB_STATUS[status] || { label: status, cls: 'bg-[#2A2A2A] text-[#D4D4D4]' };
  return (
    <span data-testid="email-job-status" className={`inline-block px-2 py-0.5 text-[11px] rounded-full font-semibold uppercase tracking-wider ${s.cls}`}>
      {s.label}
    </span>
  );
}

function fmtCountdown(ms) {
  const total = Math.max(0, Math.ceil(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

function DelaySetting() {
  const [delay, setDelay] = useState(null);
  const [allowed, setAllowed] = useState([0, 1, 2, 3, 5]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    apiAdmin.get('/admin/settings')
      .then((r) => {
        setDelay(r.data.send_delay_minutes);
        if (Array.isArray(r.data.allowed_delays)) setAllowed(r.data.allowed_delays);
      })
      .catch(() => toast.error('Failed to load email settings.'));
  }, []);

  const save = async (v) => {
    const prev = delay;
    setDelay(v);
    setSaving(true);
    try {
      await apiAdmin.put('/admin/settings', { send_delay_minutes: v });
      toast.success(v === 0 ? 'Emails now send immediately (no undo window).' : `Emails now wait ${v} minute${v === 1 ? '' : 's'} before sending.`);
    } catch (e) {
      setDelay(prev);
      toast.error(e.response?.data?.detail || 'Failed to update the delay.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <label className="inline-flex items-center gap-2 text-xs text-[var(--muted)]">
      <Clock size={13} strokeWidth={2} />
      <span className="font-semibold uppercase tracking-wider">Undo window</span>
      <select
        data-testid="email-delay-select"
        value={delay ?? ''}
        disabled={delay === null || saving}
        onChange={(e) => save(Number(e.target.value))}
        className="bg-[#141414] border border-[var(--line)] rounded-md px-2.5 py-1.5 text-xs font-semibold text-[var(--text)] focus:outline-none focus:border-[var(--silver)] disabled:opacity-50 cursor-pointer"
      >
        {delay === null ? (
          <option value="">…</option>
        ) : (
          allowed.map((v) => (
            <option key={v} value={v}>{v === 0 ? 'Send immediately' : `${v} minute${v === 1 ? '' : 's'}`}</option>
          ))
        )}
      </select>
    </label>
  );
}

export function EmailActivity({ jobs, loading, onRefresh }) {
  const [busyJob, setBusyJob] = useState(null);
  const [busyBatch, setBusyBatch] = useState(null);
  const [, forceTick] = useState(0);
  const offsetRef = useRef(0);

  // Server-truth clock: countdowns use server_time from the API, not the browser clock.
  useEffect(() => {
    if (jobs.length > 0 && jobs[0].server_time) {
      offsetRef.current = new Date(jobs[0].server_time).getTime() - Date.now();
    }
  }, [jobs]);

  const hasPending = jobs.some((j) => j.status === 'pending');
  useEffect(() => {
    if (!hasPending) return;
    const id = setInterval(() => forceTick((t) => t + 1), 500);
    return () => clearInterval(id);
  }, [hasPending]);

  const serverNow = () => Date.now() + offsetRef.current;

  const undo = async (job) => {
    setBusyJob(job.job_id);
    try {
      await apiAdmin.post(`/admin/email-jobs/${job.job_id}/cancel`);
      toast.success(`Email to ${job.recipient} cancelled — nothing was sent.`);
      onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not cancel this email.');
      onRefresh();
    } finally {
      setBusyJob(null);
    }
  };

  const undoBatch = async (batchId, count) => {
    setBusyBatch(batchId);
    try {
      const r = await apiAdmin.post('/admin/email-jobs/cancel-batch', { batch_id: batchId });
      const n = r.data.cancelled ?? count;
      toast.success(`${n} email${n === 1 ? '' : 's'} cancelled.${r.data.too_late ? ` ${r.data.too_late} already sent.` : ''}`);
      onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not cancel this batch.');
    } finally {
      setBusyBatch(null);
    }
  };

  const retry = async (job) => {
    setBusyJob(job.job_id);
    try {
      await apiAdmin.post(`/admin/email-jobs/${job.job_id}/retry`);
      toast.success(`Retrying email to ${job.recipient}…`);
      onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Retry failed.');
    } finally {
      setBusyJob(null);
    }
  };

  // Batches with 2+ still-pending emails get an "Undo all" shortcut.
  const pendingByBatch = {};
  for (const j of jobs) {
    if (j.status === 'pending' && j.batch_id) {
      pendingByBatch[j.batch_id] = (pendingByBatch[j.batch_id] || 0) + 1;
    }
  }
  const multiBatches = Object.entries(pendingByBatch).filter(([, n]) => n > 1);

  return (
    <section data-testid="email-activity-section">
      <div className="flex items-end justify-between mb-3 flex-wrap gap-3">
        <div>
          <p className="field-label mb-3">Outbox</p>
          <h2 className="serif text-3xl sm:text-4xl leading-tight">Email activity</h2>
        </div>
        <div className="flex items-center gap-4 flex-wrap">
          <DelaySetting />
          <button
            data-testid="email-activity-refresh"
            onClick={onRefresh}
            className="btn-ghost inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold"
          >
            <RefreshCw size={13} strokeWidth={2} /> Refresh
          </button>
        </div>
      </div>
      <p className="text-sm text-[var(--muted)] mb-5 max-w-3xl">
        Every invitation waits here for the undo window before it is actually sent — even if you
        close this page or sign out. Press <strong className="text-[var(--text)]">Undo send</strong> to
        stop a scheduled email; once it shows Sending or Sent it can no longer be recalled.
      </p>

      {multiBatches.length > 0 && (
        <div className="mb-4 flex flex-wrap gap-2">
          {multiBatches.map(([batchId, n]) => (
            <button
              key={batchId}
              data-testid="email-batch-undo-all-btn"
              onClick={() => undoBatch(batchId, n)}
              disabled={busyBatch === batchId}
              className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-md text-xs font-bold uppercase tracking-[0.06em] bg-rose-600 hover:bg-rose-500 text-white transition-colors disabled:opacity-50"
            >
              <MailX size={13} strokeWidth={2} />
              {busyBatch === batchId ? 'Cancelling…' : `Undo all ${n} in batch ${batchId.slice(0, 8)}`}
            </button>
          ))}
        </div>
      )}

      <div data-testid="email-activity-list" className="pnc-card rounded-lg overflow-hidden">
        {loading ? (
          <div className="p-8 text-sm text-[var(--muted)]">Loading…</div>
        ) : jobs.length === 0 ? (
          <div className="p-10 text-center">
            <p className="serif text-2xl mb-1">No emails yet</p>
            <p className="text-sm text-[var(--muted)]">Scheduled and sent invitations will appear here.</p>
          </div>
        ) : (
          <div className="divide-y divide-[var(--line)] max-h-[640px] overflow-y-auto">
            {jobs.map((j) => {
              const remaining = j.status === 'pending'
                ? new Date(j.scheduled_for).getTime() - serverNow()
                : 0;
              return (
                <div data-testid="email-job-row" key={j.job_id} className="p-5 hover:bg-[#161616] transition-colors">
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="min-w-0">
                      <div className="flex items-center gap-3 mb-1 flex-wrap">
                        <span className="font-semibold text-sm break-all">{j.recipient}</span>
                        <JobStatusPill status={j.status} />
                        {j.is_resend && (
                          <span className="px-2 py-0.5 text-[9px] rounded-full font-semibold uppercase tracking-wider bg-[#2A2A2A] text-[#D4D4D4]">Re-send</span>
                        )}
                      </div>
                      <p className="text-[11px] text-[#8A8A8A] mt-1">
                        {j.document_name || 'Contract'} · {j.contract_type === 'unique' ? 'Unique LTD' : 'Smart Solutions'}
                        {' · '}Queued {new Date(j.created_at).toLocaleString()}
                        {j.sent_at ? <> &middot; Sent {new Date(j.sent_at).toLocaleString()}</> : null}
                        {j.cancelled_at ? <> &middot; Cancelled {new Date(j.cancelled_at).toLocaleString()}</> : null}
                        {j.batch_id ? <> &middot; batch {j.batch_id.slice(0, 8)}</> : null}
                      </p>
                      {j.status === 'pending' && (
                        <p data-testid="email-job-countdown" className="text-[12px] font-semibold text-amber-300 mt-1.5 inline-flex items-center gap-1.5">
                          <Clock size={12} strokeWidth={2} />
                          {remaining <= 0 ? 'Sending any moment…' : `Sends in ${fmtCountdown(remaining)}`}
                        </p>
                      )}
                      {j.error && (
                        <p className="text-[11px] text-rose-400 mt-1 break-words">{j.error}</p>
                      )}
                    </div>
                    <div className="flex gap-2 shrink-0 items-center">
                      {j.status === 'pending' && (
                        <button
                          data-testid="email-job-undo-btn"
                          onClick={() => undo(j)}
                          disabled={busyJob === j.job_id}
                          className="inline-flex items-center gap-1.5 text-xs px-3.5 py-2 rounded-md font-bold uppercase tracking-[0.06em] bg-rose-600 hover:bg-rose-500 text-white transition-colors disabled:opacity-50"
                        >
                          <Undo2 size={13} strokeWidth={2} />
                          {busyJob === j.job_id ? 'Cancelling…' : 'Undo send'}
                        </button>
                      )}
                      {j.status === 'failed' && (
                        <button
                          data-testid="email-job-retry-btn"
                          onClick={() => retry(j)}
                          disabled={busyJob === j.job_id}
                          className="btn-ghost inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md font-semibold disabled:opacity-50"
                        >
                          <RotateCcw size={12} strokeWidth={2} />
                          {busyJob === j.job_id ? 'Retrying…' : 'Retry'}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}
